import { useEffect, useRef, useState, type ChangeEvent } from "react";
import API from "../axios";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import VehicleWheelOptions from "../components/VehicleWheelOptions";

type SortableItemProps = {
    id: string; // Must be unique!
    index: number;
    interiorPhotoStartIndex: null | number;
    image: string;
    onDelete: () => void;
    onSetInteriorIndex: () => void;
};

const SortableItem = ({
    id,
    index,
    image,
    onDelete,
    onSetInteriorIndex,
    interiorPhotoStartIndex,
}: SortableItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <button
                type="button"
                className="btn btn-grey btn-sm mx-1"
                onClick={onSetInteriorIndex}
            >
                <i
                    className={
                        interiorPhotoStartIndex == index
                            ? "bi bi-check-circle"
                            : "bi bi-circle"
                    }
                    style={{ fontSize: 14 }}
                ></i>
            </button>
            <button
                type="button"
                className="btn btn-grey btn-sm mx-1"
                onClick={onDelete}
            >
                <i className="bi bi-trash" style={{ fontSize: 14 }}></i>
            </button>
            <img
                src={image}
                width="100%"
                className="rounded-1"
                {...attributes}
                {...listeners}
            />
        </div>
    );
};

const VehicleVariantDetail = () => {
    const sensors = useSensors(useSensor(PointerSensor));
    const navigate = useNavigate();

    const [saved, setSaved] = useState(false);
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [productPhotoUploading, setProductPhotoUploading] = useState(false);
    const [galleryPhotoUploading, setGalleryPhotoUploading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    // Form fields
    const [odooId, setOdooId] = useState("");
    const [title, setTitle] = useState("");
    const [stock, setStock] = useState("");
    const [stockIncoming, setStockIncoming] = useState("");
    const [rfqStock, setRfqStock] = useState("");
    const [validStock, setValidStock] = useState("");
    const [onHandStock, setOnHandStock] = useState("");
    const [price, setPrice] = useState("");
    const [manufactrePrice, setManufacturePrice] = useState("");
    const [active, setActive] = useState("");

    // Photos
    const [productImage, setProductImage] = useState<any>("");
    const [productGallery, setProductGallery] = useState<any>([]);
    const [interiorPhotoStartIndex, setInteriorPhotoStartIndex] = useState<
        null | number
    >(0);

    const fileUploadRef = useRef<any>(null);
    const galleryUploadRef = useRef<any>(null);

    const loadVehicle = async function () {
        setLoading(true);

        const result: any = await API.getOneVehicleVariant(id as string);

        // Vehicle info
        setOdooId(result.data.odooId);
        setTitle(result.data.title);
        setStock(result.data.forecastedStock);
        setStockIncoming(result.data.incomingStock);
        setRfqStock(result.data.rfqStock);
        setValidStock(result.data.validStock);
        setOnHandStock(result.data.onHandStock);
        setProductImage(result.data.productImage);
        setProductGallery(result.data.productGallery);
        setPrice(result.data.price);
        setManufacturePrice(result.data.manufacturePrice);
        setActive(result.data.active ? "true" : "false");
        setInteriorPhotoStartIndex(result.data.interiorPhotoStartIndex || null);

        setLoading(false);
    };

    const resetFileInput = (input: HTMLInputElement | null) => {
        if (input) input.value = "";
    };

    const handleProductFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        setProductPhotoUploading(true);

        try {
            const result: any = await API.upload(
                file,
                "xcess.com/vehicles-variants"
            );
            setProductImage(result.fileUrl);
        } finally {
            setProductPhotoUploading(false);
            resetFileInput(e.target);
        }
    };

    const handleGalleryFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setGalleryPhotoUploading(true);

        try {
            const uploadedUrls: string[] = [];

            for (const file of Array.from(files)) {
                const result: any = await API.upload(
                    file,
                    "xcess.com/vehicles-variants"
                );
                uploadedUrls.push(result.fileUrl);
            }

            setProductGallery((prev: any) => [...prev, ...uploadedUrls]);
        } finally {
            setGalleryPhotoUploading(false);
            resetFileInput(e.target);
        }
    };

    const handleSave = async function () {
        setSaveLoading(true);

        const data = {
            odooId,
            title,
            stock,
            stockIncoming,
            productImage,
            productGallery,
            interiorPhotoStartIndex,
            active: active === "true",
        };

        await API.updateVehicleVariant(id as string, data);

        setSaved(true);
        setSaveLoading(false);
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (!over) return;

        const oldIndex = parseInt(active.id);
        const newIndex = parseInt(over.id);

        if (oldIndex !== newIndex) {
            setProductGallery((items: any) =>
                arrayMove(items, oldIndex, newIndex)
            );
        }
    };

    useEffect(() => {
        loadVehicle();
    }, []);

    useEffect(() => {
        setTimeout(() => {
            setSaved(false);
        }, 3000);
    }, [saved]);

    return (
        <div>
            {loading ? (
                <div
                    className="d-flex justify-content-center my-5"
                    style={{ height: "100%" }}
                >
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <Container style={{ marginTop: 100, paddingBottom: 200 }}>
                    {/* Header */}
                    <Row className="mt-5">
                        <Col
                            cols="6"
                            className="d-flex align-items-center gap-2"
                        >
                            <button
                                className="btn btn-outline-black btn-md mx-1"
                                onClick={() => navigate(-1)}
                            >
                                <i className="bi bi-chevron-left fs-4"></i>
                            </button>
                            <h3 className="fw-medium text-primary">
                                Edit Vehicle Variant
                            </h3>
                        </Col>
                        <Col cols="6" className="text-end">
                            {saved ? (
                                <button
                                    type="button"
                                    className="btn btn-success mx-1"
                                    disabled
                                >
                                    <i className="bi bi-check mx-2"></i>
                                    Saved
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-primary mx-1"
                                    onClick={handleSave}
                                    disabled={saveLoading}
                                >
                                    <i className="bi bi-save mx-2"></i>
                                    {saveLoading ? "Saving..." : "Save"}
                                </button>
                            )}
                        </Col>
                    </Row>
                    <Row className="mt-5">
                        <Col xs="12" md="8">
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    OdooId
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter product name"
                                    value={odooId}
                                    onChange={(e) => setOdooId(e.target.value)}
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Vehicle Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter product name"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Available For Sale
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter stock count"
                                    value={validStock}
                                    onChange={(e) =>
                                        setValidStock(e.target.value)
                                    }
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    RFQ Stock
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter stock count"
                                    value={rfqStock}
                                    onChange={(e) =>
                                        setRfqStock(e.target.value)
                                    }
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Forecasted
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter stock count"
                                    value={stock}
                                    onChange={(e) => setStock(e.target.value)}
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Incoming
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter product name"
                                    value={stockIncoming}
                                    onChange={(e) =>
                                        setStockIncoming(e.target.value)
                                    }
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    On Hand
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter product name"
                                    value={onHandStock}
                                    onChange={(e) =>
                                        setOnHandStock(e.target.value)
                                    }
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Price
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter product name"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Manufacture Price
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter product name"
                                    value={manufactrePrice}
                                    onChange={(e) =>
                                        setManufacturePrice(e.target.value)
                                    }
                                    disabled
                                />
                            </div>
                        </Col>
                        <Col xs="12" md="4" className="p-y-2">
                            <div>
                                <div className="d-flex flex-row justify-content-between">
                                    <label className="form-label fw-bold">
                                        Product Photo
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-grey btn-sm mx-1"
                                        onClick={() => setProductImage("")}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                                {productImage ? (
                                    <img
                                        src={productImage}
                                        width="100%"
                                        className="rounded-2"
                                    />
                                ) : (
                                    <>
                                        <div
                                            className="bg-body-secondary rounded-2 d-flex justify-content-center align-items-center"
                                            style={{
                                                width: "100%",
                                                height: 250,
                                                cursor: "pointer",
                                            }}
                                            onClick={() => {
                                                fileUploadRef.current?.click();
                                            }}
                                        >
                                            <i className="bi bi-upload mx-3"></i>
                                            <small>
                                                {productPhotoUploading
                                                    ? "Uploading ..."
                                                    : "Upload new photo"}
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="divider"></div>
                            <div className="mt-3">
                                <div className="d-flex flex-row justify-content-between">
                                    <label className="form-label fw-bold">
                                        Product Gallery
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={() => {
                                            galleryUploadRef.current?.click();
                                        }}
                                        disabled={galleryPhotoUploading}
                                    >
                                        <i className="bi bi-upload mx-2"></i>
                                        {galleryPhotoUploading
                                            ? "Uploading ..."
                                            : "Upload New Photo"}
                                    </button>
                                </div>
                                <div className="d-flex flex-row my-2">
                                    <p>
                                        Images with the{" "}
                                        <i className="bi bi-check-circle blue-text"></i>{" "}
                                        will show first when user select with
                                        interior color.
                                    </p>
                                </div>
                                <div>
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext
                                            items={productGallery.map(
                                                (
                                                    image: string,
                                                    index: number
                                                ) => index.toString()
                                            )}
                                            strategy={
                                                verticalListSortingStrategy
                                            }
                                        >
                                            <Row>
                                                {productGallery.map(
                                                    (
                                                        image: string,
                                                        index: number
                                                    ) => (
                                                        <Col
                                                            xs="4"
                                                            className="text-end"
                                                            key={index}
                                                        >
                                                            <SortableItem
                                                                id={index.toString()}
                                                                index={index}
                                                                interiorPhotoStartIndex={
                                                                    interiorPhotoStartIndex
                                                                }
                                                                image={image}
                                                                onSetInteriorIndex={() => {
                                                                    setInteriorPhotoStartIndex(
                                                                        index
                                                                    );
                                                                }}
                                                                onDelete={() => {
                                                                    const newGallery =
                                                                        [
                                                                            ...productGallery,
                                                                        ];
                                                                    newGallery.splice(
                                                                        index,
                                                                        1
                                                                    );
                                                                    setProductGallery(
                                                                        newGallery
                                                                    );
                                                                }}
                                                            />
                                                        </Col>
                                                    )
                                                )}
                                            </Row>
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            )}

            <input
                type="file"
                ref={fileUploadRef}
                onChange={handleProductFileChange}
                style={{ display: "none" }}
            />
            <input
                type="file"
                ref={galleryUploadRef}
                onChange={handleGalleryFileChange}
                style={{ display: "none" }}
                multiple
            />
        </div>
    );
};

export default VehicleVariantDetail;
